import { SendEventDtoType } from "@cede/types";

export type ProcessingBatch = {
  events: SendEventDtoType[];
  generation: number;
};

/** In-memory event buffer with identity-generation isolation and retry-safe leases. */
export class MetricsEventsQueue {
  private readonly inFlightByGeneration = new Map<number, SendEventDtoType[]>();
  private readonly listeners = new Set<() => void>();
  private readonly processingGenerations = new Set<number>();
  private _eventQueue: SendEventDtoType[] = [];
  private _generation = 0;
  private identityKey: string | undefined;
  private lastChanceSend: Promise<void> | null = null;

  get generation() {
    return this._generation;
  }

  get pendingCount() {
    return this._eventQueue.length;
  }

  enqueue(...events: SendEventDtoType[]) {
    if (events.length === 0) return;
    this._eventQueue.push(...events);
    this.listeners.forEach((listener) => listener());
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  snapshot(): SendEventDtoType[] {
    return [...this._eventQueue];
  }

  /**
   * Includes the active request by default, so unload can duplicate it with keepalive.
   * That copy is only safe under the SAME identity, where Mixpanel dedups the shared
   * `$insert_id`; a caller stamping another one must exclude it.
   */
  currentGenerationSnapshot({ includeInFlight = true }: { includeInFlight?: boolean } = {}): ProcessingBatch {
    const inFlight = includeInFlight ? (this.inFlightByGeneration.get(this._generation) ?? []) : [];

    return {
      events: [...inFlight, ...this._eventQueue],
      generation: this._generation,
    };
  }

  /** Private: dropping without bumping the generation is the identity leak this class prevents. */
  private clear() {
    this._eventQueue.length = 0;
  }

  /** Preserves pending handshake events while isolating transports configured for another identity. */
  alignIdentity(identityKey: string) {
    if (this.identityKey === undefined) {
      this.identityKey = identityKey;
    } else if (this.identityKey !== identityKey) {
      this._generation += 1;
      this.identityKey = identityKey;
    }

    return this._generation;
  }

  /** Drops the prior identity's pending events without blocking the next generation. Returns how many were dropped. */
  invalidate(): number {
    const droppedCount = this._eventQueue.length;
    this._generation += 1;
    this.identityKey = undefined;
    this.clear();
    return droppedCount;
  }

  shouldBeProcessed() {
    return this._eventQueue.length > 0 && !this.processingGenerations.has(this._generation);
  }

  beginProcessing(): ProcessingBatch | null {
    if (!this.shouldBeProcessed()) return null;

    const generation = this._generation;
    const events = this._eventQueue;
    this._eventQueue = [];
    this.processingGenerations.add(generation);
    this.inFlightByGeneration.set(generation, events);

    return { events, generation };
  }

  completeProcessing(generation: number) {
    this.inFlightByGeneration.delete(generation);
    this.processingGenerations.delete(generation);
  }

  /** Re-queues a failed batch at the front. Returns how many were dropped because identity moved on. */
  restoreProcessing(generation: number): number {
    const events = this.inFlightByGeneration.get(generation) ?? [];
    this.completeProcessing(generation);
    if (generation !== this._generation) return events.length;
    if (events.length > 0) this._eventQueue.unshift(...events);
    return 0;
  }

  /**
   * An unload send may carry the pending events under another distinct_id, and
   * Mixpanel dedups only within one; an ordinary flush waits for it to settle so it
   * only sends what that send did not deliver.
   */
  holdForLastChanceSend(settled: Promise<unknown>) {
    const hold: Promise<void> = settled
      .then(
        () => undefined,
        () => undefined,
      )
      .finally(() => {
        if (this.lastChanceSend === hold) this.lastChanceSend = null;
      });
    this.lastChanceSend = hold;
  }

  /** Null when nothing is in flight, so the ordinary flush keeps its synchronous lease. */
  get pendingLastChanceSend(): Promise<void> | null {
    return this.lastChanceSend;
  }

  /** Removes only events confirmed by a successful transport. */
  acknowledge(generation: number, events: SendEventDtoType[]) {
    if (events.length === 0) return;
    const acknowledged = new Set(events);
    const inFlight = this.inFlightByGeneration.get(generation);
    if (inFlight)
      this.inFlightByGeneration.set(
        generation,
        inFlight.filter((event) => !acknowledged.has(event)),
      );
    if (generation === this._generation) {
      this._eventQueue = this._eventQueue.filter((event) => !acknowledged.has(event));
    }
  }
}
